/**
 * MaterializationNotices — what happened to the checks the coach just added.
 *
 * Every sentence in here is written by the backend notice catalogue
 * (`backend/app/config/coach_notices.yaml`, served through `MaterializationResult.notices`)
 * and is rendered VERBATIM. The only copy this component owns is its heading
 * and the three severity labels in `utils/materialization.ts` ("Not added" /
 * "Added with a note" / "Worth a look"). It never falls back to a guessed
 * sentence: a notice with no text shows nothing rather than inventing
 * something (REFACTOR_PLAN §C.3d / §C.7 — one catalogue, checked by the Python
 * denylist test over the catalogue itself).
 *
 * It renders notices about anything the coach asked for — a check, a focus
 * area, a phase, a common mistake — because the materialiser can refuse any of
 * them and `source_ref` names which (`suggested_goal:` / `body_part_emphasis:`
 * / `coaching_phase:` / `common_mistake:`).
 *
 * Why it exists: the old flow appended whatever the client managed to build and
 * said nothing about the rest. An axis check that came out of extraction with
 * one body point instead of two was accepted, stored, and then failed on every
 * single evaluation — 3 of 12 of one coach's checkpoints were permanently dead
 * and the report called it a temporary measurement error (audit items E2E-5 and
 * E2E-6). Now a check is either added or listed here with a reason.
 */

import { ExclamationTriangleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import Badge from '@/components/ui/Badge';
import type { Notice } from '@/types/api';
import {
  SEVERITY_LABELS,
  SEVERITY_VARIANTS,
  sortNoticesBySeverity,
} from '@/utils/materialization';

/** One notice: the coach's heading, then what / why / how, each verbatim. */
export function NoticeRow({ notice }: { notice: Notice }) {
  const Icon = notice.severity === 'info' ? InformationCircleIcon : ExclamationTriangleIcon;
  return (
    <li className="rounded-lg border border-border-subtle bg-dark-elevated-2 p-3 space-y-1.5">
      <div className="flex items-start gap-2 flex-wrap">
        <Icon
          className={
            notice.severity === 'dropped'
              ? 'h-4 w-4 text-danger flex-shrink-0 mt-0.5'
              : notice.severity === 'degraded'
                ? 'h-4 w-4 text-warning flex-shrink-0 mt-0.5'
                : 'h-4 w-4 text-info flex-shrink-0 mt-0.5'
          }
          aria-hidden="true"
        />
        <Badge variant={SEVERITY_VARIANTS[notice.severity]} size="sm" className="flex-shrink-0 mt-0.5">
          {SEVERITY_LABELS[notice.severity]}
        </Badge>
        {notice.label && (
          <p className="text-sm font-medium text-text-primary min-w-0 break-words">
            {notice.label}
          </p>
        )}
      </div>
      {notice.what && <p className="text-sm text-text-primary">{notice.what}</p>}
      {notice.why && <p className="text-xs text-text-secondary">{notice.why}</p>}
      {notice.how && <p className="text-xs text-text-muted">{notice.how}</p>}
    </li>
  );
}

interface MaterializationNoticesProps {
  notices: Notice[];
  className?: string;
}

/**
 * The list the coach reads after adding checks. Renders nothing when there is
 * nothing to say.
 */
export default function MaterializationNotices({
  notices,
  className,
}: MaterializationNoticesProps) {
  if (notices.length === 0) return null;

  const sorted = sortNoticesBySeverity(notices);
  // Count ITEMS, not notices: one item can carry more than one note, and
  // "2 of these could not be added" has to mean two things, not two sentences.
  const notAdded = new Set(
    sorted.filter((n) => n.severity === 'dropped').map((n) => n.source_ref),
  ).size;

  return (
    <section
      className={className}
      aria-label="What we added, and what we couldn't"
    >
      <p className="text-sm font-semibold text-text-primary mb-1">
        What we added, and what we couldn&apos;t
      </p>
      {/*
        Deliberately says nothing about how many checks carry a note: the
        backend's `summary.degraded` counts (check x shortfall) PAIRS, so one
        check with two shortfalls counts twice, and a list that mixes checks,
        focus areas, phases and mistakes cannot claim "N checks" either.
      */}
      <p className="text-xs text-text-muted mb-2">
        {notAdded > 0
          ? `${notAdded} of these could not be added. Each one says why, and what to try instead.`
          : 'Nothing was left out. Each note below is worth a read.'}
      </p>
      <ul className="space-y-2">
        {sorted.map((notice, i) => (
          <NoticeRow key={`${notice.source_ref}-${notice.kind}-${i}`} notice={notice} />
        ))}
      </ul>
    </section>
  );
}
